import { useEffect, useState, ReactNode } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Spinner } from '@lancar-pos/ui';
import { useAuthStore } from '../lib';
import { useSettingsStore } from '../hooks';

interface ProtectedRouteProps {
  children: ReactNode;
  requireLicense?: boolean;
  requireOnboarding?: boolean;
}

export function ProtectedRoute({ 
  children, 
  requireLicense = false, 
  requireOnboarding = true 
}: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, isOffline, initialize, checkLicense } = useAuthStore();
  const { getSetting, isReady: settingsReady } = useSettingsStore();
  const [licenseChecked, setLicenseChecked] = useState(false);
  const [licenseValid, setLicenseValid] = useState(false);

  const onboardingCompleted = getSetting('onboarding_completed') === true;

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    async function verifyLicense() {
      // Skip license check if VITE_LICENSE_REQUIRED is 'false'
      if (import.meta.env.VITE_LICENSE_REQUIRED === 'false') {
        setLicenseValid(true);
        setLicenseChecked(true);
        return;
      }

      if (!authLoading && isAuthenticated && requireLicense && !isOffline) {
        const { valid } = await checkLicense();
        setLicenseValid(valid);
        setLicenseChecked(true);
      } else if (isOffline) {
        setLicenseValid(true);
        setLicenseChecked(true);
      }
    }
    
    verifyLicense();
  }, [authLoading, isAuthenticated, requireLicense, isOffline, checkLicense]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const currentPath = location.pathname;
      const search = currentPath !== '/app/login' ? { redirect: currentPath } : undefined;
      navigate({
        to: '/app/login',
        replace: true,
        search,
      });
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname]);

  useEffect(() => {
    if (location.pathname === '/app/onboarding' || location.pathname === '/app/sheets-setup') {
      return;
    }
    
    if (!authLoading && isAuthenticated && requireOnboarding && settingsReady && !onboardingCompleted) {
      navigate({
        to: '/app/onboarding',
        replace: true,
      });
    }
  }, [authLoading, isAuthenticated, requireOnboarding, onboardingCompleted, navigate, location.pathname, settingsReady]);

  useEffect(() => {
    if (licenseChecked && !licenseValid && requireLicense) {
      navigate({
        to: '/app/dashboard',
        replace: true,
      });
    }
  }, [licenseChecked, licenseValid, requireLicense, navigate]);

  if (authLoading || !settingsReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Memuat...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (requireOnboarding && !onboardingCompleted) {
    return null;
  }

  if (requireLicense && !licenseChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Memverifikasi lisensi...</p>
        </div>
      </div>
    );
  }

  if (requireLicense && !licenseValid) {
    return null;
  }

  return <>{children}</>;
}
